import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgDotsHorizontalTriple } from '@actual-app/components/icons/v1';
import { theme } from '@actual-app/components/theme';
import { Tooltip } from '@actual-app/components/tooltip';
import { View } from '@actual-app/components/view';
import type {
  AccountEntity,
  AccountGroupEntity,
} from '@actual-app/core/types/models';

import { useMoveAccountMutation } from '#accounts';
import {
  accountDragType,
  getAccountDropTarget,
  getAccountGroupSections,
} from '#accounts/account-groups';
import { isAccountFailedSync } from '#accounts/syncStatus';
import {
  useAccountGroups,
  useCollapsedAccountGroups,
  useCollapsedAccountSections,
} from '#hooks/useAccountGroups';
import type { CollapsibleAccountSection } from '#hooks/useAccountGroups';
import { useAccounts } from '#hooks/useAccounts';
import { useClosedAccounts } from '#hooks/useClosedAccounts';
import { useLocalPref } from '#hooks/useLocalPref';
import { useOffBudgetAccounts } from '#hooks/useOffBudgetAccounts';
import { useOnBudgetAccounts } from '#hooks/useOnBudgetAccounts';
import { useUpdatedAccounts } from '#hooks/useUpdatedAccounts';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch, useSelector } from '#redux';
import * as bindings from '#spreadsheet/bindings';

import { Account } from './Account';
import { SecondaryItem } from './SecondaryItem';

const fontWeight = 600;

export function getSidebarAccountGroupSections(
  items: AccountEntity[],
  groups: AccountGroupEntity[],
  classification: 'onbudget' | 'offbudget' | 'closed',
) {
  return getAccountGroupSections(items, groups, classification);
}

export function Accounts() {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const { data: accounts = [] } = useAccounts();
  const updatedAccounts = useUpdatedAccounts();
  const { data: offbudgetAccounts = [] } = useOffBudgetAccounts();
  const { data: onBudgetAccounts = [] } = useOnBudgetAccounts();
  const { data: closedAccounts = [] } = useClosedAccounts();
  const { data: groups, isFetched: groupsFetched } = useAccountGroups();
  const syncingAccountIds = useSelector(state => state.account.accountsSyncing);
  const dispatch = useDispatch();
  const activeGroups = groups?.filter(group => !group.tombstone) ?? [];
  const collapsedGroups = useCollapsedAccountGroups(
    groupsFetched ? activeGroups.map(group => group.id) : undefined,
  );
  const collapsedSections = useCollapsedAccountSections();

  const getAccountPath = (account: AccountEntity) => `/accounts/${account.id}`;

  const [showClosedAccounts, setShowClosedAccountsPref] = useLocalPref(
    'ui.showClosedAccounts',
  );

  function onDragChange(drag: { state: string }) {
    setIsDragging(drag.state === 'start');
  }

  const moveAccount = useMoveAccountMutation();

  const makeDropPadding = (i: number) => {
    if (i === 0) {
      return {
        paddingTop: isDragging ? 15 : 0,
        marginTop: isDragging ? -15 : 0,
      };
    }
    return undefined;
  };

  async function onReorder(
    id: string,
    dropPos: 'top' | 'bottom' | null,
    targetId: string,
    bucket: AccountEntity[] = accounts,
    targetGroup: AccountEntity['group'] | undefined = undefined,
  ) {
    const targetIdToMove = getAccountDropTarget(bucket, dropPos, targetId);

    const dragged = accounts.find(account => account.id === id);
    const target = accounts.find(account => account.id === targetId);
    const group =
      targetGroup !== undefined
        ? targetGroup
        : dragged && target && dragged.group !== target.group
          ? target.group
          : undefined;
    await moveAccount.mutateAsync({ id, targetId: targetIdToMove, group });
  }

  const onToggleClosedAccounts = () => {
    setShowClosedAccountsPref(!showClosedAccounts);
  };

  const renderAccounts = (items: AccountEntity[]) =>
    items.map((account, i) => (
      <Account
        key={account.id}
        name={account.name}
        account={account}
        connected={!!account.bank}
        pending={syncingAccountIds.includes(account.id)}
        failed={isAccountFailedSync(account)}
        updated={updatedAccounts.includes(account.id)}
        to={getAccountPath(account)}
        query={bindings.accountBalance(account.id)}
        onDragChange={onDragChange}
        onDrop={(id, dropPos, targetId) =>
          onReorder(id, dropPos, targetId, items)
        }
        outerStyle={makeDropPadding(i)}
        hasActiveGroups={activeGroups.length > 0}
      />
    ));

  const renderSectionToggle = (
    section: CollapsibleAccountSection,
    label: string,
  ) => {
    const collapsed = collapsedSections.isCollapsed(section);
    return (
      <Button
        variant="bare"
        aria-label={
          collapsed
            ? t('Expand {{section}} accounts', { section: label })
            : t('Collapse {{section}} accounts', { section: label })
        }
        aria-expanded={!collapsed}
        onPress={() => collapsedSections.toggle(section)}
        style={{ color: theme.sidebarItemText, paddingRight: 10 }}
      >
        {collapsed ? '▸' : '▾'}
      </Button>
    );
  };

  const renderGroupedAccounts = (
    classification: 'onbudget' | 'offbudget' | 'closed',
    items: AccountEntity[],
  ) => {
    const sections = groups
      ? getSidebarAccountGroupSections(items, groups, classification)
      : null;
    if (!sections) return renderAccounts(items);
    return sections.flatMap(section => {
      const key = section.group?.id ?? `${classification}-ungrouped`;
      const collapsed = section.group
        ? collapsedGroups.isCollapsed(section.group.id)
        : false;
      return [
        <SecondaryItem
          key={`${key}-header`}
          title={section.group?.name ?? t('Ungrouped')}
          onClick={() =>
            section.group && collapsedGroups.toggle(section.group.id)
          }
          color={section.group?.color ?? theme.sidebarItemText}
          titleStyle={{ fontSize: 15, fontWeight: 700 }}
          balance={
            section.group
              ? bindings.accountGroupBalance(section.group.id, classification)
              : undefined
          }
          dropId={key}
          testId={
            section.group ? `account-group-${section.group.id}` : undefined
          }
          dropTypes={accountDragType(classification)}
          onDrop={(id, dropPos, targetId) =>
            onReorder(
              id,
              dropPos,
              targetId,
              section.accounts,
              section.group?.id ?? null,
            )
          }
          rightContent={section.group ? (collapsed ? '▸' : '▾') : undefined}
          actions={
            section.group ? (
              <Tooltip content={t('Manage account groups')}>
                <Button
                  variant="bare"
                  aria-label={t('Manage account groups')}
                  onPress={() =>
                    dispatch(pushModal({ modal: { name: 'account-groups' } }))
                  }
                >
                  <SvgDotsHorizontalTriple width={14} height={14} />
                </Button>
              </Tooltip>
            ) : undefined
          }
        />,
        ...(collapsed ? [] : renderAccounts(section.accounts)),
      ];
    });
  };

  return (
    <View
      style={{
        flexGrow: 1,
        '@media screen and (max-height: 480px)': {
          minHeight: 'auto',
        },
      }}
    >
      <View
        style={{
          height: 1,
          backgroundColor: theme.sidebarItemBackgroundHover,
          marginTop: 15,
          flexShrink: 0,
        }}
      />

      <View style={{ overflow: 'auto' }}>
        <Account
          name={t('All accounts')}
          to="/accounts"
          query={bindings.allAccountBalance()}
          style={{ fontWeight, marginTop: 15 }}
          nameStyle={{ fontSize: 15 }}
          isExactPathMatch
          balanceTestId="sidebar-all-accounts-balance"
          hasActiveGroups={activeGroups.length > 0}
        />

        {onBudgetAccounts.length > 0 && (
          <Account
            name={t('On budget')}
            to="/accounts/onbudget"
            query={bindings.onBudgetAccountBalance()}
            style={{
              fontWeight,
              marginTop: 13,
              marginBottom: 5,
            }}
            nameStyle={{ fontSize: 15 }}
            actions={renderSectionToggle('onbudget', t('On budget'))}
            titleAccount
            balanceTestId="sidebar-on-budget-balance"
          />
        )}

        {!collapsedSections.isCollapsed('onbudget') &&
          renderGroupedAccounts('onbudget', onBudgetAccounts)}

        {offbudgetAccounts.length > 0 && (
          <Account
            name={t('Off budget')}
            to="/accounts/offbudget"
            query={bindings.offBudgetAccountBalance()}
            style={{
              fontWeight,
              marginTop: 13,
              marginBottom: 5,
            }}
            nameStyle={{ fontSize: 15 }}
            actions={renderSectionToggle('offbudget', t('Off budget'))}
            titleAccount
            balanceTestId="sidebar-off-budget-balance"
          />
        )}

        {!collapsedSections.isCollapsed('offbudget') &&
          renderGroupedAccounts('offbudget', offbudgetAccounts)}

        {closedAccounts.length > 0 && (
          <SecondaryItem
            style={{ marginTop: 15 }}
            title={
              showClosedAccounts
                ? t('Closed accounts')
                : t('Closed accounts...')
            }
            onClick={onToggleClosedAccounts}
            bold
            titleStyle={{ fontSize: 15 }}
          />
        )}

        {showClosedAccounts && renderGroupedAccounts('closed', closedAccounts)}
      </View>
    </View>
  );
}
